import { normalizeCompletionPolicy } from '../../completionPolicy';
import {
    DEFAULT_PHYSICAL_SCALE,
    normalizeRulerPhysicalScaleConfig,
    validatePhysicalScaleConfig,
} from '../RulerMeasurementUI/rulerMeasurementModel';
import { RULE_TYPES } from '../BoundingBoxUI/validation';

export { DEFAULT_PHYSICAL_SCALE };

const RULE_TYPE_IDS = new Set(RULE_TYPES.map((r) => r.id));
const UNIT_IDS = new Set(['px', 'µm', 'auto']);

function toTrimmedString(v) {
    return typeof v === 'string' ? v.trim() : '';
}

/** Allowed single-key hotkeys: digits and Latin letters (stored lowercase). */
const HOTKEY_CHAR = /^[0-9a-z]$/i;

/** Letters reserved for point UI mode shortcuts (not valid category hotkeys). */
const UI_MODE_RESERVED_CATEGORY_LETTERS = new Set(['d', 'm', 'r']);

export function isUiReservedCategoryHotkeyKey(ch) {
    return typeof ch === 'string' && UI_MODE_RESERVED_CATEGORY_LETTERS.has(ch.toLowerCase());
}

export function getUiReservedCategoryHotkeyMessage(letterLower) {
    const L = letterLower.toLowerCase();
    if (L === 'd') return 'This key is reserved for Draw mode (press D in the viewer). Choose another digit or letter.';
    if (L === 'm') return 'This key is reserved for Modify mode (press M in the viewer). Choose another digit or letter.';
    if (L === 'r') return 'This key is reserved for Review mode (press R in the viewer). Choose another digit or letter.';
    return 'This key is reserved for a viewer shortcut. Choose another digit or letter.';
}

export function getCategoryHotkeyConfigHelpText() {
    return [
        'Optional: one digit (0–9) or letter (a–z) per category. In the viewer, use it in Draw to pick the draw class, and in Modify or Review to reclassify the selection.',
        'Reserved for the viewer (not available as category hotkeys): D / M / R switch Draw / Modify / Review; Esc returns to View.',
    ].join(' ');
}

export const CATEGORY_HOTKEY_INVALID_CHARACTER_MESSAGE =
    'Use a single digit (0–9) or letter (a–z). Letters D, M, and R are reserved for the viewer.';

export function normalizeCategoryHotkey(raw) {
    const s = toTrimmedString(raw);
    if (!s || s.length !== 1 || !HOTKEY_CHAR.test(s)) return null;
    if (/[0-9]/.test(s)) return s;
    const lower = s.toLowerCase();
    if (UI_MODE_RESERVED_CATEGORY_LETTERS.has(lower)) return null;
    return lower;
}

export const DEFAULT_POINT_CONFIG = {
    instructions: '',
    categories: [],
    physicalScale: DEFAULT_PHYSICAL_SCALE,
};

export function nextPointCategoryId(categories) {
    const used = new Set(
        (Array.isArray(categories) ? categories : [])
            .map((c) => toTrimmedString(c?.id))
            .filter(Boolean)
    );
    let n = 1;
    while (used.has(`category-${n}`)) n += 1;
    return `category-${n}`;
}

export function createDraftPointCategory(categories) {
    return {
        id: nextPointCategoryId(categories),
        label: '',
        style: {
            strokeColor: '#000000',
            fillColor: '#000000',
            strokeWidth: 2,
            fillOpacity: 1,
        },
    };
}

function normalizeRule(r) {
    const type = toTrimmedString(r?.type);
    if (!RULE_TYPE_IDS.has(type)) return null;
    const v = Number(r?.value);
    if (!Number.isFinite(v)) return null;
    const unit = toTrimmedString(r?.unit);
    const u = UNIT_IDS.has(unit) ? unit : 'px';
    return { type, value: v, unit: u };
}

function isCssColorString(v) {
    return typeof v === 'string' && v.trim().length > 0;
}

function isFiniteNumber(v) {
    return Number.isFinite(v);
}

function normalizeOneCategory(c, index, seenIds) {
    const preferredId = toTrimmedString(c?.id);
    const fallbackId = `category-${index + 1}`;
    let id = preferredId || fallbackId;
    if (seenIds.has(id)) {
        let k = 2;
        while (seenIds.has(`${id}-${k}`)) k += 1;
        id = `${id}-${k}`;
    }
    seenIds.add(id);
    const label = toTrimmedString(c?.label) || id;
    const rulesRaw = Array.isArray(c?.rules) ? c.rules : [];
    const rules = rulesRaw.map(normalizeRule).filter(Boolean);
    const hotkey = normalizeCategoryHotkey(c?.hotkey);
    const out = {
        id,
        label,
        style: (() => {
            const rawStyle = c?.style && typeof c.style === 'object' ? c.style : {};
            const strokeColor = typeof rawStyle.strokeColor === 'string' ? rawStyle.strokeColor : '#000000';
            const fillColor = typeof rawStyle.fillColor === 'string' ? rawStyle.fillColor : strokeColor;
            const strokeWidth = Number.isFinite(Number(rawStyle.strokeWidth)) ? Number(rawStyle.strokeWidth) : 2;
            const fillOpacity = Number.isFinite(Number(rawStyle.fillOpacity)) ? Number(rawStyle.fillOpacity) : 1;
            return {
                ...rawStyle,
                strokeColor,
                fillColor,
                strokeWidth,
                fillOpacity,
            };
        })(),
        rules,
    };
    if (hotkey) out.hotkey = hotkey;
    return out;
}

export function normalizePointCategories(rawCategories) {
    const list = Array.isArray(rawCategories) ? rawCategories : [];
    const seen = new Set();
    return list.map((c, i) => normalizeOneCategory(c, i, seen));
}

export function normalizePointConfig(config) {
    const raw = config && typeof config === 'object' ? config : {};
    const categories = normalizePointCategories(raw.categories);
    const out = {
        ...DEFAULT_POINT_CONFIG,
        ...raw,
        instructions: typeof raw.instructions === 'string' ? raw.instructions : '',
        categories,
        physicalScale: normalizeRulerPhysicalScaleConfig(raw.physicalScale),
        completionPolicy: normalizeCompletionPolicy(raw),
    };
    if (raw.roi && typeof raw.roi === 'object') {
        out.roi = raw.roi;
    }
    if (raw.import && typeof raw.import === 'object') {
        out.import = raw.import;
    }
    return out;
}

/**
 * @returns {{code: string, message: string}[]}
 */
export function validatePointConfig(config) {
    const issues = [];
    const raw = config && typeof config === 'object' ? config : {};
    const cats = Array.isArray(raw.categories) ? raw.categories : [];

    if (cats.length === 0) {
        issues.push({
            code: 'missing_categories',
            message: 'Add at least one point category in Annotation UI configuration.',
        });
    }

    const seen = new Set();
    const seenHotkeys = new Set();
    cats.forEach((c, idx) => {
        const id = toTrimmedString(c?.id);
        const label = toTrimmedString(c?.label);
        const row = idx + 1;
        if (!id) {
            issues.push({ code: 'empty_category_id', message: `Point category ${row} is missing an id.` });
        } else if (seen.has(id)) {
            issues.push({ code: 'duplicate_category_id', message: `Point category id "${id}" is duplicated.` });
        } else {
            seen.add(id);
        }
        if (id && !label) {
            issues.push({ code: 'missing_category_label', message: `Point category "${id}" is missing a label.` });
        }

        const hotkeyRaw = toTrimmedString(c?.hotkey);
        if (hotkeyRaw) {
            if (hotkeyRaw.length === 1 && HOTKEY_CHAR.test(hotkeyRaw) && isUiReservedCategoryHotkeyKey(hotkeyRaw)) {
                const letter = hotkeyRaw.toLowerCase();
                issues.push({
                    code: 'reserved_category_hotkey',
                    message: id
                        ? `Category "${id}": ${getUiReservedCategoryHotkeyMessage(letter)}`
                        : `Point category ${row}: ${getUiReservedCategoryHotkeyMessage(letter)}`,
                });
            } else {
                const hk = normalizeCategoryHotkey(c?.hotkey);
                if (!hk) {
                    issues.push({
                        code: 'invalid_category_hotkey',
                        message: id
                            ? `Category "${id}": hotkey must be a single digit or letter (a–z), and not D, M, or R (reserved for modes).`
                            : `Point category ${row}: hotkey must be a single digit or letter (a–z), and not D, M, or R (reserved for modes).`,
                    });
                } else if (seenHotkeys.has(hk)) {
                    issues.push({
                        code: 'duplicate_category_hotkey',
                        message: id
                            ? `Category "${id}": hotkey "${hk}" is already assigned to another category.`
                            : `Point category ${row}: hotkey "${hk}" is duplicated.`,
                    });
                } else {
                    seenHotkeys.add(hk);
                }
            }
        }

        const style = c?.style && typeof c.style === 'object' ? c.style : null;
        if (style) {
            if ('strokeColor' in style && !isCssColorString(style.strokeColor)) {
                issues.push({
                    code: 'invalid_style_strokeColor',
                    message: id
                        ? `Category "${id}": strokeColor must be a non-empty string.`
                        : `Point category ${row}: strokeColor must be a non-empty string.`,
                });
            }
            if ('fillColor' in style && !isCssColorString(style.fillColor)) {
                issues.push({
                    code: 'invalid_style_fillColor',
                    message: id
                        ? `Category "${id}": fillColor must be a non-empty string.`
                        : `Point category ${row}: fillColor must be a non-empty string.`,
                });
            }
            if ('strokeWidth' in style) {
                const w = Number(style.strokeWidth);
                if (!isFiniteNumber(w) || w <= 0) {
                    issues.push({
                        code: 'invalid_style_strokeWidth',
                        message: id
                            ? `Category "${id}": strokeWidth must be a positive number.`
                            : `Point category ${row}: strokeWidth must be a positive number.`,
                    });
                }
            }
            if ('fillOpacity' in style) {
                const o = Number(style.fillOpacity);
                if (!isFiniteNumber(o) || o < 0 || o > 1) {
                    issues.push({
                        code: 'invalid_style_fillOpacity',
                        message: id
                            ? `Category "${id}": fillOpacity must be a number between 0 and 1.`
                            : `Point category ${row}: fillOpacity must be a number between 0 and 1.`,
                    });
                }
            }
        }
    });

    issues.push(...validatePhysicalScaleConfig(raw.physicalScale));
    return issues;
}

